<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">首页板块设计</h2>
          <p class="text-sm text-neutral-500 mt-1">
            决定首页显示哪些板块、板块标题和先后顺序，
            <span class="text-primary">Web 端与小程序端同步生效</span>
            <span class="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
              当前启用 {{ enabledCount }} / {{ list.length }} 个
            </span>
          </p>
        </div>
        <button
          @click="getList"
          class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
          title="刷新列表"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>

      <div v-loading="loading" class="space-y-3">
        <div
          v-for="(s, index) in list"
          :key="s.id"
          class="flex flex-col sm:flex-row sm:items-center gap-3 bg-neutral-50 rounded-xl border border-neutral-200 p-4"
          :class="s.status === 1 ? '' : 'opacity-60'"
        >
          <!-- 序号 + 板块说明 -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <span class="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                {{ index + 1 }}
              </span>
              <h3 class="font-bold text-neutral-900 truncate">
                {{ sectionName(s.sectionKey) }}
              </h3>
              <span
                class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                :class="s.status === 1
                  ? 'bg-green-100 text-green-700'
                  : 'bg-neutral-200 text-neutral-500'"
              >
                {{ s.status === 1 ? '显示' : '隐藏' }}
              </span>
            </div>
            <div class="text-sm text-neutral-500 truncate">
              {{ s.title || '（使用默认标题）' }}
              <span v-if="s.subtitle" class="text-neutral-400"> · {{ s.subtitle }}</span>
            </div>
          </div>

          <!-- 操作 -->
          <div class="flex flex-wrap items-center gap-2">
            <button
              @click="moveUp(index)"
              :disabled="index === 0 || loading"
              class="px-3 py-2 rounded-lg border border-neutral-300 text-sm hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="上移"
            >
              <i class="fa fa-arrow-up"></i>
            </button>
            <button
              @click="moveDown(index)"
              :disabled="index === list.length - 1 || loading"
              class="px-3 py-2 rounded-lg border border-neutral-300 text-sm hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="下移"
            >
              <i class="fa fa-arrow-down"></i>
            </button>
            <button
              @click="toggleStatus(s)"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="s.status === 1
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'"
            >
              <i class="fa mr-1" :class="s.status === 1 ? 'fa-eye-slash' : 'fa-eye'"></i>
              {{ s.status === 1 ? '隐藏' : '显示' }}
            </button>
            <button
              @click="openEdit(s)"
              class="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 text-sm font-medium transition-colors"
            >
              <i class="fa fa-edit mr-1"></i>编辑
            </button>
          </div>
        </div>

        <div v-if="!loading && list.length === 0" class="text-center py-10 text-neutral-400">
          暂无板块配置
        </div>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog v-model="dialogVisible" title="编辑板块" width="520px">
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">板块</label>
          <input
            :value="sectionName(form.sectionKey)"
            type="text"
            disabled
            class="w-full px-4 py-2 rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-500 text-sm"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">标题（留空用默认标题）</label>
          <input
            v-model="form.title"
            type="text"
            maxlength="64"
            placeholder="如：热门AI工具"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">副标题（可选）</label>
          <input
            v-model="form.subtitle"
            type="text"
            maxlength="128"
            placeholder="板块下方的说明文字"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">排序号</label>
            <input
              v-model.number="form.sortNum"
              type="number"
              placeholder="数字越小越靠前"
              class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">状态</label>
            <select
              v-model="form.status"
              class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            >
              <option :value="1">显示</option>
              <option :value="0">隐藏</option>
            </select>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            @click="dialogVisible = false"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50 text-sm"
          >
            取消
          </button>
          <button
            @click="save"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
          >
            确定
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";

const SECTION_NAMES = {
  hero: "顶部横幅（大标题 + 按钮 + 轮播图）",
  ask: "小义问宝 AI 助手",
  agents: "AI 智能体",
  prompts: "提示词 · 技巧分享",
  quicknav: "快捷入口（小程序端）",
  tools: "热门 AI 工具",
  requests: "最新需求",
  categories: "按分类浏览",
  subjects: "按学科浏览",
  leaderboard: "工具排行榜",
  software: "软件分享",
};

export default {
  name: "HomeSectionManager",
  data() {
    return {
      loading: false,
      list: [],
      dialogVisible: false,
      form: {
        id: "",
        sectionKey: "",
        title: "",
        subtitle: "",
        sortNum: 0,
        status: 1,
      },
    };
  },
  computed: {
    enabledCount() {
      return this.list.filter((s) => s.status === 1).length;
    },
  },
  created() {
    this.getList();
  },
  methods: {
    sectionName(key) {
      return SECTION_NAMES[key] || key;
    },
    async getList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({});
        const res = await this.$axios({
          url: "home/admin/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.list = res.data || [];
        }
      } catch (e) {
        console.error("获取首页板块失败:", e);
        this.list = [];
      } finally {
        this.loading = false;
      }
    },
    openEdit(s) {
      this.form = {
        id: s.id,
        sectionKey: s.sectionKey,
        title: s.title || "",
        subtitle: s.subtitle || "",
        sortNum: s.sortNum || 0,
        status: s.status,
      };
      this.dialogVisible = true;
    },
    async save() {
      try {
        const data = await this.$aesEncrypt(this.form);
        const res = await this.$axios({
          url: "home/admin/save",
          method: "PUT",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success("保存成功");
          this.dialogVisible = false;
          this.getList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请重试");
      }
    },
    async toggleStatus(s) {
      try {
        const payload = {
          id: s.id,
          sectionKey: s.sectionKey,
          title: s.title || "",
          subtitle: s.subtitle || "",
          sortNum: s.sortNum || 0,
          status: s.status === 1 ? 0 : 1,
        };
        const data = await this.$aesEncrypt(payload);
        const res = await this.$axios({
          url: "home/admin/save",
          method: "PUT",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success(s.status === 1 ? "已隐藏" : "已显示");
          this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败，请重试");
      }
    },
    async saveSort(ids) {
      try {
        const data = await this.$aesEncrypt({ ids });
        const res = await this.$axios({
          url: "home/admin/sort",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success("排序已保存");
          this.getList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请重试");
      }
    },
    moveUp(index) {
      if (index <= 0) return;
      const ids = this.list.map((s) => s.id);
      const tmp = ids[index - 1];
      ids[index - 1] = ids[index];
      ids[index] = tmp;
      this.saveSort(ids);
    },
    moveDown(index) {
      if (index >= this.list.length - 1) return;
      const ids = this.list.map((s) => s.id);
      const tmp = ids[index + 1];
      ids[index + 1] = ids[index];
      ids[index] = tmp;
      this.saveSort(ids);
    },
  },
};
</script>
